import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { Field } from "../../components/ui/Field";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import type { Site } from "../../lib/api/models";
import { refreshInventory } from "../../lib/queries";

const AREA =
  "min-h-28 w-full rounded-md border border-line bg-surface px-2.5 py-2 text-base text-ink placeholder:text-ink-3 focus:border-green focus:outline-none";

/** The notes of a site: the one thing about a site that is kept in Docs (Gorelo sync §4.2). `site` null: closed. */
export function SiteNotesDialog({
  clientId,
  site,
  onClose,
}: {
  clientId: string;
  site: Site | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!site) return;
    setNotes(site.notes ?? "");
    setError(null);
  }, [site]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!site) return;
    setBusy(true);
    try {
      await api.sites.updateNotes(site.id, notes.trim());
      toast(`Notes of “${site.name}” saved.`, "success");
      await refreshInventory(queryClient, clientId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The notes could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={site !== null} onClose={onClose} title={`Notes of ${site?.name ?? "the site"}`} size="sm">
      <form onSubmit={submit} noValidate className="grid gap-4">
        {error ? (
          <p role="alert" className="rounded-md bg-danger-wash px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
        <p className="text-sm text-ink-3">The name and the address come from Gorelo. Change them there.</p>
        <Field label="Notes" hint="Access, opening hours, who to ask on site. No passwords or other secrets.">
          {(id, describedBy) => (
            <textarea
              id={id}
              aria-describedby={describedBy}
              maxLength={4000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={AREA}
            />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy}>
            Save notes
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
